import React, { useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useIntl } from 'react-intl'
import { useLocation, useMatch } from 'react-router-dom'
import messages from './messages'
import { messages as routes } from 'routes'
import copywriting from 'copywriting'
import useUrl from 'useUrl'
import { useBrand } from 'contexts/BrandContext'
import { rebrandText } from 'brands/rebrand'

export const Meta = ({ language, ...props }) => {
  const { locale, formatMessage } = useIntl()
  const brand = useBrand()
  const location = useLocation()
  const { pathname } = location
  const match = useMatch(pathname)

  const [title, description] = useMemo(() => {
    const { title, description } = copywriting(locale, match.pathname) || {}
    const { metaTitle, metaDescription, metaTitleDefault, metaDescriptionDefault } = messages

    return [
      rebrandText(brand, title) || (language
        ? formatMessage(metaTitle, { language })
        : formatMessage(metaTitleDefault)),
      rebrandText(brand, description) || (language
        ? formatMessage(metaDescription, { language })
        : formatMessage(metaDescriptionDefault)),
    ]
  }, [locale, match.pathname, language, brand])

  const currentUrl = useUrl(locale, location.pathname, true, false, brand)
  const ogLocale = locale.includes('-')
    ? `${locale.split('-')[0]}_${locale.split('-')[1].toUpperCase()}`
    : locale

  if (
    location.search !== '' &&
    (match.path === formatMessage(routes.exploreByLanguage) ||
      match.path === routes.exploreByLanguage.defaultMessage)
  ) {
    return (
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
    )
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={brand.ogImage} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={brand.siteName} />
      <meta property="og:locale" content={ogLocale} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={brand.ogImage} />
      {brand.twitterHandle && <meta name="twitter:site" content={brand.twitterHandle} />}
    </Helmet>
  )
}

export default Meta
